import { getFaviconUrl } from '@heidi/utils';

import type { ChatItem, ToolUseItem } from '../../../../../contracts/harness.types';
import { isEvidenceSubagentToolName } from '../../../../../data/projects/components/EvidenceSessionInlineBlock.utils';

/** A source an Evidence search returned: enough to name it and show its publisher. */
export interface EvidenceSource {
  /**
   * What the app knows the source by, which it lists and opens it under: the
   * URL for a web page, the document for a library document. Defaults to the URL.
   */
  id?: string;
  url: string;
  title?: string;
  /** The publisher's logical domain when it is not the URL's host (integration partners). */
  domain?: string;
  /** A partner's own logo, which beats the favicon when present. */
  logo?: string;
  /**
   * Found in the clinician's own library: one of their documents (no URL) or a
   * page from one of their web collections. It is counted apart and named as theirs.
   */
  fromLibrary?: boolean;
}

/** What tells two sources apart: its id, else its URL. */
export const sourceKey = (source: EvidenceSource): string => source.id ?? source.url;

/** One literature search an Evidence step launched. */
export interface EvidenceSearch {
  query: string;
  /** The search has returned. */
  done: boolean;
}

/**
 * What an Evidence step searched for and found, as the Working block shows it.
 * Evidence's session lives app-side, so the app maps it into this shape.
 */
export interface EvidenceTrail {
  /** In the order they were launched. */
  searches: readonly EvidenceSearch[];
  /** Every source its searches have returned so far, in that order. */
  returned: readonly EvidenceSource[];
  /**
   * Every source the step's searches returned, duplicates removed, with any
   * library documents after them: what its "N sources found" row lists. Only a
   * completed step has it.
   */
  found: readonly EvidenceSource[] | null;
}

/**
 * What decides where an Evidence step's trail comes from, and whether it can
 * still change: a settled step's saved result is the one thing that can settle
 * it for good.
 */
export type EvidenceStep = Pick<
  ToolUseItem,
  'id' | 'status' | 'childAgentRunId' | 'result_provider_metadata'
>;

/**
 * Where the Working block reads each Evidence step's trail. Evidence's sessions
 * and child runs are app state, so the app binds this: `subscribe` watches only
 * what can still change for that step, and `read` returns the same trail until
 * it does.
 */
export interface EvidenceTrails {
  subscribe: (step: EvidenceStep, onChange: () => void) => () => void;
  read: (step: ToolUseItem) => EvidenceTrail | null;
  /**
   * Opens one of a settled step's sources; `citationNumber` is its place in the
   * list, from 1. Opening is Evidence's (viewer, library, tracking), so the app
   * binds it once; without it the row only names the sources.
   */
  openSource?: (
    step: ToolUseItem,
    source: EvidenceSource,
    citationNumber: number
  ) => void;
}

/**
 * Whether the block draws a call as an Evidence step: an Evidence call that
 * has not failed or been cancelled. Those keep their plain row, which says so.
 */
export const isEvidenceStep = (item: ChatItem): item is ToolUseItem =>
  item.kind === 'tool_use' &&
  isEvidenceSubagentToolName(item.name) &&
  item.status !== 'error' &&
  item.status !== 'cancelled';

/** How many publisher logos stand for a set of sources. */
export const LOGO_WINDOW = 3;

const hostOf = (url: string): string | null => {
  try {
    return new URL(url).hostname || null;
  } catch {
    return null;
  }
};

export const sourceDomain = (source: EvidenceSource): string | null =>
  source.domain || hostOf(source.url);

/**
 * The image that stands for a source's publisher: its partner logo, else its
 * site's favicon. Only a host ever reaches the favicon service, so a source
 * with no logo and no absolute URL, like a partner's proxied document, has none.
 */
export const sourceLogo = (source: EvidenceSource): string | null => {
  if (source.logo) return source.logo;
  const host = hostOf(source.url);
  return host ? getFaviconUrl(`https://${host}`) : null;
};

/** A publisher behind a set of sources, as its logo shows it. */
export interface Publisher {
  domain: string;
  logo: string;
}

/**
 * One publisher per domain, in the order the searches ranked their sources: a
 * domain enters at its best-placed source, and one with no image is left out.
 */
export const publishersOf = (
  sources: readonly EvidenceSource[]
): readonly Publisher[] => {
  const seen = new Set<string>();
  const publishers: Publisher[] = [];
  for (const source of sources) {
    const domain = sourceDomain(source);
    const logo = sourceLogo(source);
    if (!domain || !logo || seen.has(domain)) continue;
    seen.add(domain);
    publishers.push({ domain, logo });
  }
  return publishers;
};
